import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { defer, fromEvent, Subscription } from 'rxjs';

import { OrderDetailsShot } from '../../../../models/common/order-details-shot.model';
import { OrderStatus } from '../../../../models/common/order-status.models';
import { SidebarTypeEnum } from '../../../../models/common/sidebar-type.enum';
import { PhotographerOrderDetails } from '../../models/photographer-order-details.model';
import { UploadedShot } from '../../models/uploaded-shot.model';
import { PhotographerService } from '../../../../services/photographer.service';
import { BasicFile } from '../../../../shared/models/basic-file.model';
import { ShotStatusEnum } from '../../../../shared/models/shot-status.enum';
import { TopbarService } from '../../../../shared/services/topbar.service';

@Component({
  standalone: false,
  selector: 'bt-photographer-order-info',
  templateUrl: './photographer-order-info.component.html',
  styleUrls: ['./photographer-order-info.component.scss']
})
export class PhotographerOrderInfoComponent implements OnChanges, OnDestroy {

  @Input() public orderId: number;
  @Input() public orderNumber: string;
  @Input() public modelId: number;

  @Output() public exit = new EventEmitter<any>();

  public orderDetails: { orderStatus?: OrderStatus, data?: PhotographerOrderDetails[], dataToView?: PhotographerOrderDetails[], selected?: PhotographerOrderDetails, search: string, totalLength?: number, areFiltered?: boolean } = {
    search: ''
  };
  public shotStatusEnum = ShotStatusEnum;
  public uploadedShots: UploadedShot[];
  public tmpShot: UploadedShot;
  public basicFile: BasicFile;
  public currentSidebarType: SidebarTypeEnum;
  public sidebarTypeEnum = SidebarTypeEnum;
  public orderDetailsLoaderId = 'orderDetailsLoaderId';
  public uploadedShotsLoaderId = 'uploadedShotsLoaderId';
  public sidebarLoaderId = 'sidebarLoaderId';

  // drag&drop variables
  public inDragging: boolean;
  public elementInDrag: HTMLElement;
  public elementInDragId = 'elementInDragId';
  public overedData: { shot?: OrderDetailsShot, style?: PhotographerOrderDetails } = {};

  private subscription = new Subscription();

  constructor(
    private photoService: PhotographerService,
    private topbarService: TopbarService
  ) {
    this.searchTextSubscription();
  }

  @HostListener('window:dragover', ['$event'])
  public windowDragover(ev: any) {
    this.overedData = {};
    this.onDragOver(ev);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.orderId) {
      if (this.orderId) {
        this.getPhotographerOrderDetails();
        this.getPhotographerUploadedShots();
      } else {
        this.orderDetails.data = null;
      }
    }
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public onStyleComments(item: PhotographerOrderDetails) {
    item.showMenu = false;
    this.orderDetails.selected = item;
    this.currentSidebarType = SidebarTypeEnum.shotsComments;
  }

  public setStylesToView(fromSearch?: boolean) {
    const search = this.orderDetails.search ? this.orderDetails.search.toLowerCase() : '';
    this.orderDetails.dataToView = this.orderDetails.data.filter(x => x.fullName.toLowerCase().indexOf(search) >= 0);
    this.orderDetails.areFiltered = !!this.orderDetails.search && (this.orderDetails.areFiltered || fromSearch);
  }

  public onShowShotPreview(s: UploadedShot) {
    s.showMenu = false;
    this.basicFile = new BasicFile({ FileName: s.fileName, FilePath: s.imgPath });
  }

  public onDeleteUploadedShot(s: UploadedShot) {
    s.showMenu = false;
    this.tmpShot = s;
    this.currentSidebarType = SidebarTypeEnum.deleteAction;
  }

  public onDragStart(ev: any, s: UploadedShot) {
    this.tmpShot = s;
    const sub = defer(() => fromEvent(ev.target as HTMLElement, 'dragend')).subscribe(() => {
      this.onDragEnd();
      sub.unsubscribe();
    });
    this.inDragging = true;
    const ghostEl = ev.target.cloneNode(true) as HTMLElement;
    if (ghostEl && ghostEl.style) {
      ghostEl.style.opacity = '0';
      ghostEl.style.display = 'none';
      ev.dataTransfer.setDragImage(ghostEl, 0, 0);
    }
    setTimeout(() => { this.elementInDrag = document.getElementById(this.elementInDragId); });
  }

  public onDragOver(ev: any, shot?: OrderDetailsShot, style?: PhotographerOrderDetails) {
    ev.stopPropagation();
    if (shot && shot.shotApprovalStateId !== ShotStatusEnum.Approved) {
      ev.preventDefault();
      this.overedData = { shot, style };
    }
    this.dragElement(this.elementInDrag, ev);
  }

  public exitFromSidebar() {
    this.currentSidebarType = null;
    this.tmpShot = null;
    this.overedData = {};
  }

  public assignShot() {
    this.photoService.assignShot(this.tmpShot.orderImageId, this.overedData.shot.shotId, this.sidebarLoaderId).subscribe(() => {
      this.exitFromSidebar();
      this.getPhotographerOrderDetails();
      this.getPhotographerUploadedShots();
    });
  }

  public onImagesUpload(event: any) {
    const files: FileList = event.target.files;
    this.photoService.uploadShot(files, this.orderId, this.orderDetailsLoaderId).subscribe(n => {
      if (n !== undefined) { this.getPhotographerUploadedShots(); }
    });
  }

  public deleteShot() {
    this.photoService.deleteShot(this.tmpShot.orderImageId, this.sidebarLoaderId).subscribe(() => {
      this.currentSidebarType = null;
      this.tmpShot = null;
      this.getPhotographerUploadedShots();
    });
  }

  public onBack() {
    this.exit.emit();
  }

  public onOrderComments() {
    this.currentSidebarType = SidebarTypeEnum.orderComments;
  }

  private dragElement(elementInDrag: HTMLElement, event: any) {
    if (elementInDrag) {
      elementInDrag.style.left = (event.clientX + 15) + 'px';
      elementInDrag.style.top = (event.clientY + 15) + 'px';
    }
  }

  private onDragEnd() {
    this.overedData.shot && this.overedData.style ? this.currentSidebarType = SidebarTypeEnum.customSidebar : this.overedData = {};
    this.inDragging = false;
    this.elementInDrag = null;
  }

  private searchTextSubscription() {
    this.subscription.add(this.topbarService.searchTextSubscribe.subscribe(data => {
      if (data !== this.orderDetails.search) {
        this.orderDetails.search = data;
        this.setStylesToView(true);
      }
    }));
  }

  private getPhotographerOrderDetails() {
    this.photoService.getPhotographerOrderDetails(this.orderId, this.orderDetailsLoaderId).subscribe(data => {
      this.orderDetails.data = data.orderDetails;
      this.orderDetails.orderStatus = data.orderStatus;
      let index = -1;
      if (this.modelId) index = this.orderDetails.data.findIndex(x => x.modelId === this.modelId);
      this.setStylesToView();
    });
  }

  private getPhotographerUploadedShots() {
    this.photoService.getPhotographerUploadedShots(this.orderId, this.uploadedShotsLoaderId).subscribe(data => this.uploadedShots = data);
  }
}
